/**
 * Gets validation rules for a form field based on the ruleset and credentials
 * @param {string} fieldName - The name of the field to get rules for
 * @param {Object} ruleSet - The ruleset containing validation rules
 * @param {Object} options - Additional options for validation
 * @param {Object} options.credentials - Credentials configuration for NPI validation
 * @param {string} options.formKey - Key to access specific form rules (e.g., 'form_req_rep')
 * @returns {Object} Validation rules object for react-hook-form
 */
export const getFieldRules = (fieldName, ruleSet, options = {}) => {
  const { credentials = {}, formKey = 'form_req_rep' } = options;
  const rules = ruleSet[formKey]?.rules || [];
  const validationRules = {};

  rules.forEach(rule => {
    if (rule.fields.includes(fieldName)) {
      // Handle required validation - Special case for npiNumber
      if (rule.name === 'required') {
        if (fieldName !== 'npiNumberCreds') { // Only add required validation for non-NPI fields
          validationRules.required = {
            value: true,
            message: rule.messages[fieldName]
          };
        }
      }
      // Handle pattern validation
      if (rule.validators && rule.validators[0] instanceof RegExp) {
        validationRules.pattern = {
          value: rule.validators[0],
          message: rule.messages[fieldName]
        };
      }
    }
  });

  // Special validation for npiNumber
  if (fieldName === 'npiNumberCreds') {
    validationRules.validate = (value, formValues) => {
      // If no value is provided
      if (!value) {
        // Check if a credential is selected that requires NPI
        const selectedCredential = formValues.credential;
        if (selectedCredential && credentials[selectedCredential]?.requiresNPI || !selectedCredential) {
          return 'Please enter your NPI number.';
        }
        return true;
      }

      return true;
    };
  } else {
    // Custom validation function for other fields
    validationRules.validate = (value) => {
      if (!value) return true; // Skip if empty (required rule will handle this)

      // Find all rules for this field
      const fieldRules = rules.filter(rule =>
        rule.fields.includes(fieldName) && rule.validators
      );

      // Check all validators
      for (const rule of fieldRules) {
        if (rule.validators[0] instanceof RegExp) {
          if (!rule.validators[0].test(value)) {
            return rule.messages[fieldName];
          }
        }
      }

      return true;
    };
  }

  return validationRules;
};
